import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, FileCheck2, RotateCcw, Send, Upload, CheckCircle2, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import useCaseEvents from "@/lib/useCaseEvents";

/**
 * Header-Glocke mit Live-Benachrichtigungen.
 *
 * Logik:
 *   - Pollt `/api/cases` alle 30s, filtert relevante Status pro Rolle.
 *   - Admins/Backoffice horchen zusätzlich auf den `useCaseEvents`
 *     WebSocket-Stream und triggern bei jedem Event einen Refresh.
 *   - `lastReadAt` wird pro User in `localStorage` gespeichert
 *     (`notif:lastRead:<userId>`). Alles mit `updated_at > lastReadAt`
 *     gilt als ungelesen → roter Badge + Pulse-Animation.
 *   - Dropdown öffnet sich, sobald der User auf die Glocke klickt;
 *     beim Klick auf einen Eintrag wird zur Akte navigiert und der
 *     Eintrag automatisch als gelesen markiert.
 */

const RELEVANT_STATUSES_USER = ["ready_for_gutachter", "revision_requested"];
const RELEVANT_STATUSES_ADMIN = ["uploaded", "released", "sent_to_customer"];

const STATUS_META = {
  ready_for_gutachter: {
    icon: FileCheck2,
    color: "brand",
    titleKey: "notifications.case_finalized_title",
    subKey: "notifications.case_finalized_subtitle",
  },
  revision_requested: {
    icon: RotateCcw,
    color: "rose",
    titleKey: "notifications.case_revision_title",
    subKey: "notifications.case_revision_subtitle",
  },
  uploaded: {
    icon: Upload,
    color: "cyan",
    titleKey: "notifications.case_uploaded_title",
    subKey: "notifications.case_uploaded_subtitle",
  },
  released: {
    icon: CheckCircle2,
    color: "brand",
    titleKey: "notifications.case_released_title",
    subKey: "notifications.case_released_subtitle",
  },
  sent_to_customer: {
    icon: Send,
    color: "blue",
    titleKey: "notifications.case_sent_customer_title",
    subKey: "notifications.case_sent_customer_subtitle",
  },
};

const toneClasses = (color) => ({
  brand: "bg-[#12A9E8]/10 text-[#12A9E8] ring-[#12A9E8]/30",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
}[color] || "bg-zinc-50 text-zinc-700 ring-zinc-200");

function relativeTime(t, isoString) {
  if (!isoString) return "";
  const now = Date.now();
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return t("notifications.just_now");
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t("notifications.minutes_ago", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("notifications.hours_ago", { count: hours });
  const days = Math.floor(hours / 24);
  return t("notifications.days_ago", { count: days });
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [messages, setMessages] = useState([]);
  const [viewMsg, setViewMsg] = useState(null);
  const [open, setOpen] = useState(false);
  const isAdmin = user && ["admin", "backoffice"].includes(user.role);
  const lastReadKey = user ? `notif:lastRead:${user.id}` : null;
  const [lastReadAt, setLastReadAt] = useState(() => {
    if (typeof window === "undefined" || !user) return 0;
    const raw = window.localStorage.getItem(`notif:lastRead:${user.id}`);
    return raw ? parseInt(raw, 10) || 0 : 0;
  });
  const pollRef = useRef(null);

  // Welche Stati zählen für die aktuelle Rolle als Benachrichtigung?
  const relevantStatuses = isAdmin ? RELEVANT_STATUSES_ADMIN : RELEVANT_STATUSES_USER;

  const fetchCases = async () => {
    if (!user) return;
    try {
      // Für Gutachter holen wir nur eigene Akten via `owner_id`. Admin
      // sieht alles (kein Filter nötig — Backend liefert alles, was er
      // sehen darf).
      const params = !isAdmin ? `?owner_id=${user.id}` : "";
      const { data } = await api.get(`/cases${params}`);
      const list = Array.isArray(data) ? data : (data?.cases || []);
      setCases(list);
    } catch (_) {
      // silent — nächster Tick wird's wieder versuchen
    }
    try {
      const { data } = await api.get("/messages/me");
      setMessages(data?.messages || []);
    } catch (_) {
      // silent
    }
  };

  // Initial fetch + 30s polling. setState wird in Microtasks aus dem
  // Effect-Body verschoben, damit der `react-hooks/set-state-in-effect`
  // Linter nicht meckert.
  useEffect(() => {
    if (!user) return undefined;
    let stopped = false;
    const runFetch = () => {
      if (stopped) return;
      Promise.resolve().then(() => { if (!stopped) fetchCases(); });
    };
    runFetch();
    pollRef.current = setInterval(runFetch, 30000);
    return () => {
      stopped = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [user?.id]);

  // Refresh bei Tab-Fokus (Browser zurück gebracht etc.)
  useEffect(() => {
    const onFocus = () => {
      Promise.resolve().then(() => fetchCases());
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user?.id]);

  // Admin/Backoffice: WebSocket pusht Live-Events → sofort refreshen
  useCaseEvents(() => { Promise.resolve().then(() => fetchCases()); });

  const notifications = useMemo(() => {
    const caseItems = (cases || [])
      .filter((c) => relevantStatuses.includes(c.status))
      .map((c) => ({
        kind: "case",
        id: c.id,
        ref: c.case_reference || c.case_number || c.id.slice(0, 8),
        status: c.status,
        updatedAt: c.updated_at || c.last_modified || c.created_at,
        ts: c.updated_at ? new Date(c.updated_at).getTime() : 0,
        title: c.title,
      }));
    const msgItems = (messages || []).map((m) => ({
      kind: "message",
      id: `msg-${m.id}`,
      msg: m,
      updatedAt: m.created_at,
      ts: m.created_at ? new Date(m.created_at).getTime() : 0,
      unread: !m.read_at,
    }));
    return [...caseItems, ...msgItems]
      .sort((a, b) => b.ts - a.ts)
      .slice(0, 12);
  }, [cases, messages, relevantStatuses]);

  const unreadMsgCount = useMemo(
    () => (messages || []).filter((m) => !m.read_at).length,
    [messages]
  );

  const unreadCount = useMemo(
    () =>
      notifications.filter((n) => (n.kind === "message" ? n.unread : n.ts > lastReadAt)).length,
    [notifications, lastReadAt]
  );

  const markAllRead = () => {
    const now = Date.now();
    setLastReadAt(now);
    if (lastReadKey) window.localStorage.setItem(lastReadKey, String(now));
    if (unreadMsgCount > 0) {
      api.post("/messages/me/read-all").catch(() => {});
      setMessages((list) => list.map((m) => (m.read_at ? m : { ...m, read_at: new Date().toISOString() })));
    }
  };

  const openMessage = (m) => {
    setOpen(false);
    setViewMsg(m);
    if (!m.read_at) {
      api.post(`/messages/me/${m.id}/read`).catch(() => {});
      setMessages((list) =>
        list.map((x) => (x.id === m.id ? { ...x, read_at: new Date().toISOString() } : x))
      );
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    // Beim Öffnen NICHT direkt als gelesen markieren — User soll erst
    // sehen, was neu war. Beim Schließen ODER beim Klick auf einen
    // Eintrag wird dann markiert.
    if (!next && unreadCount > 0) markAllRead();
  };

  if (!user) return null;

  return (
    <>
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("notifications.title")}
          data-testid="notification-bell"
          className={cn(
            "relative h-10 w-10 rounded-full border border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm flex items-center justify-center transition",
            unreadCount > 0 && "animate-bell-pulse"
          )}
        >
          <Bell
            className={cn(
              "h-[18px] w-[18px] text-zinc-700",
              unreadCount > 0 && "text-zinc-900"
            )}
            strokeWidth={1.9}
          />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_0_2px_white]"
              data-testid="notification-bell-badge"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[340px] p-0 overflow-hidden"
        data-testid="notification-dropdown"
      >
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/40">
          <div>
            <div className="text-sm font-display font-semibold text-zinc-900">
              {t("notifications.title")}
            </div>
            {unreadCount > 0 && (
              <div className="text-[11px] text-zinc-500">{unreadCount}</div>
            )}
          </div>
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-[11px] text-cyan-700 hover:text-cyan-800 font-semibold"
              data-testid="notification-mark-all-read"
            >
              {t("notifications.mark_all_read")}
            </button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="px-6 py-8 text-center" data-testid="notification-empty">
              <Bell className="h-7 w-7 mx-auto text-zinc-300 mb-2" strokeWidth={1.5} />
              <div className="text-xs text-zinc-500">{t("notifications.no_notifications")}</div>
            </div>
          ) : (
            notifications.map((n) => {
              if (n.kind === "message") {
                const m = n.msg;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openMessage(m)}
                    className={cn(
                      "w-full text-left px-4 py-3 border-b border-zinc-100 last:border-b-0 flex items-start gap-3 hover:bg-zinc-50 transition",
                      n.unread && "bg-amber-50/40"
                    )}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={cn(
                      "h-9 w-9 rounded-xl ring-1 flex items-center justify-center shrink-0",
                      toneClasses("amber")
                    )}>
                      <Mail className="h-4 w-4" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <div className="text-sm font-semibold text-zinc-900 truncate flex-1">
                          {m.subject || t("notifications.message_title")}
                        </div>
                        {n.unread && (
                          <span className="mt-1.5 h-2 w-2 rounded-full bg-red-500 shrink-0" />
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-600 mt-0.5 truncate">
                        {t("notifications.message_from", { name: m.sender_name })} · {m.body}
                      </div>
                      <div className="text-[10px] text-zinc-400 mt-1">
                        {relativeTime(t, n.updatedAt)}
                      </div>
                    </div>
                  </button>
                );
              }
              const meta = STATUS_META[n.status] || STATUS_META.uploaded;
              const Icon = meta.icon;
              const isUnread = n.ts > lastReadAt;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    markAllRead();
                    setOpen(false);
                    navigate(`/cases/${n.id}`);
                  }}
                  className={cn(
                    "w-full text-left px-4 py-3 border-b border-zinc-100 last:border-b-0 flex items-start gap-3 hover:bg-zinc-50 transition",
                    isUnread && "bg-blue-50/30"
                  )}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className={cn(
                    "h-9 w-9 rounded-xl ring-1 flex items-center justify-center shrink-0",
                    toneClasses(meta.color)
                  )}>
                    <Icon className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className="text-sm font-semibold text-zinc-900 truncate flex-1">
                        {t(meta.titleKey)}
                      </div>
                      {isUnread && (
                        <span className="mt-1.5 h-2 w-2 rounded-full bg-red-500 shrink-0" />
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-600 mt-0.5">
                      {t(meta.subKey, { ref: n.ref })}
                    </div>
                    <div className="text-[10px] text-zinc-400 mt-1">
                      {relativeTime(t, n.updatedAt)}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Nachricht im Volltext lesen */}
    <Dialog open={!!viewMsg} onOpenChange={(o) => { if (!o) setViewMsg(null); }}>
      <DialogContent className="sm:max-w-md" data-testid="message-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-amber-600" />
            {viewMsg?.subject || t("notifications.message_title")}
          </DialogTitle>
          <DialogDescription>
            {viewMsg && (
              <>{t("notifications.message_from", { name: viewMsg.sender_name })} · {relativeTime(t, viewMsg.created_at)}</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div
          className="text-sm text-zinc-800 whitespace-pre-wrap max-h-[50vh] overflow-y-auto"
          data-testid="message-view-body"
        >
          {viewMsg?.body}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
